"use client";

import { trackComponent } from "@repo/analytics";
import {
  dlCtaTextConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import BFLButton from "@repo/shared-ui/BFLButton";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  selectLeadStatus,
  selectLoginStatus,
  useAppSelector,
} from "@repo/web-store";
import React, { FC, useCallback, useEffect, useRef } from "react";
import { redirectWithUtmParams } from "../../components/Demat/DematUtils";
import { handleSSORedirect } from "../../components/Demat/DematAccount";
import ListWithShowMore from "../../components/News/components/List";
import TrendingStockCard from "./components/TrendingStockCard";
import { useTrendingStocksPlp } from "./hooks/useTrendingStocksPlp";
import type {
  TrendingStocksCardData,
  TrendingStocksDurationFilter,
  TrendingStocksEntityMap,
  TrendingStocksFieldLabels,
  TrendingStocksInitialSelections,
  TrendingStocksPrimaryTab,
  TrendingStocksWatchlistConfig,
  TrendingStockTradeAction,
} from "./types";
import {
  TRENDING_STOCKS_DURATION_FILTERS,
  TRENDING_STOCKS_PRIMARY_TABS,
} from "./types";
import { getSelectionKey } from "./utils/trendingStocksHelpers";
import { trendingStocksTrackAnalytics } from "./utils/trendingStocksCarouselAnalytics";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import NoResultsFound from "../../components/NoResultsFound";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";

interface TrendingStocksPlpUiConfig {
  tabs: {
    stocks: string;
    fo: string;
  };
  durationFilters: Record<TrendingStocksDurationFilter, string>;
  trade: {
    buyText: string;
    sellText: string;
    headerName: string;
    onboardingUrlWeb?: string;
    onboardingUrlApp?: string;
  };
  showMoreText: string;
  showLessText: string;
  fields?: TrendingStocksFieldLabels;
  watchlist?: TrendingStocksWatchlistConfig;
  emptyState?: {
    title?: string;
    description?: string;
    imageSrc?: string;
  };
  apiFailingState?: {
    title?: string;
    description?: string;
    retryButtonText?: string;
    errorImage?: string;
    apiFailureImageAlt?: string;
  };
}

export interface TrendingStocksPlpInteractiveClientProps {
  initialItems: TrendingStocksCardData[];
  initialTotalCount: number;
  initialShowMore: boolean;
  initialTab: TrendingStocksPrimaryTab;
  initialDuration: TrendingStocksDurationFilter;
  initialHasError?: boolean;
  initialSelections?: TrendingStocksInitialSelections;
  entityMap?: TrendingStocksEntityMap;
  defaultEntity?: string;
  initialExpandedCards?: number;
  uiConfig: TrendingStocksPlpUiConfig;
}

const TrendingStocksPlpInteractiveClient: FC<TrendingStocksPlpInteractiveClientProps> = ({
  initialItems,
  initialTotalCount,
  initialShowMore,
  initialTab,
  initialDuration,
  initialHasError = false,
  initialSelections,
  entityMap,
  defaultEntity,
  initialExpandedCards,
  uiConfig,
}) => {
  const stocksSectionRef = useRef<HTMLElement>(null);
  const loginStatus = useAppSelector(selectLoginStatus);
  const leadStatus = useAppSelector(selectLeadStatus);

  const isLoggedIn = loginStatus === "success" || loginStatus === "perpetual";
  const hasBFSLAccount = leadStatus === "ETB";
  const canShowStocksTab = isLoggedIn;
  const canShowFoTab = isLoggedIn && hasBFSLAccount;

  const onboardingUrl = isOpenInApp()
    ? (uiConfig.trade.onboardingUrlApp ?? uiConfig.trade.onboardingUrlWeb ?? "")
    : (uiConfig.trade.onboardingUrlWeb ?? "");

  const {
    activeTab,
    setActiveTab,
    activeDuration,
    setActiveDuration,
    items,
    showMore,
    showLess,
    isLoading,
    isLoadingMore,
    hasError,
    handleShowMore,
    handleShowLess,
    retryCurrentSelection,
  } = useTrendingStocksPlp({
    initialItems,
    initialTotalCount,
    initialShowMore,
    initialTab,
    initialDuration,
    initialSelections,
    entityMap,
    defaultEntity,
    initialExpandedCards,
  });

  useEffect(() => {
    if (!canShowFoTab && activeTab === "fo") {
      setActiveTab("stocks");
    }
  }, [activeTab, canShowFoTab, setActiveTab]);

  const handleTradeClick = useCallback(
    async (action: TrendingStockTradeAction, stock: TrendingStocksCardData) => {
      if (!isLoggedIn || !hasBFSLAccount) {
        if (onboardingUrl) {
          redirectWithUtmParams(onboardingUrl, "false");
        }
        return;
      }

      const exchange = stock.exchange === 2 ? 2 : 0;
      const exchId = stock.exchId || (exchange === 2 ? "2" : "0");
      const nseToken = stock.nseToken || (exchange === 0 ? stock.token : 0);
      const bseToken = stock.bseToken || (exchange === 2 ? stock.token : 0);

      await handleSSORedirect(
        action,
        loginStatus,
        exchId,
        stock.symbol,
        nseToken,
        bseToken,
        uiConfig.trade.headerName,
      );
    },
    [hasBFSLAccount, isLoggedIn, loginStatus, onboardingUrl, uiConfig.trade.headerName],
  );

  const trackShowMoreClick = useCallback(() => {
    void (async () => {
      try {
        const customerId = await getCustomerIdForAnalytics();
        const sectionTitle =
          uiConfig.watchlist?.section_title ?? "Trending stocks";
        const trackPayload = {
          componentName: trendingStocksTrackAnalytics.componentNamePlp,
          sectionTitle,
          eventType: dlEventConstants.CLICK,
          clickType: "stocks_market_card_sku_details_button_click",
          ctaText:dlCtaTextConstants.SHOW_MORE,
          productCategory: trendingStocksTrackAnalytics.productCategory,
          cleverTapOverrides: {
            EP_PROPERTY_TYPE: trendingStocksTrackAnalytics.propertyType,
            EP_PRODUCT_NAME: trendingStocksTrackAnalytics.productName,
            EP_CAMPAIGN: trendingStocksTrackAnalytics.campaign,
            EP_SPRINT: trendingStocksTrackAnalytics.sprint, 
            EP_REFERENCE_ID: trendingStocksTrackAnalytics.referenceId,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            EP_ENTRYPOINT: trendingStocksTrackAnalytics.entryPoints.plp,
          },
        };
        await trackComponent(trackPayload);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track trending stocks show more click", error);
      }
    })();
  }, [uiConfig.showMoreText, uiConfig.watchlist?.section_title]);

  const scrollToDefaultView = useCallback(() => {
    const section = stocksSectionRef.current;
    if (!section) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        section.scrollIntoView({ behavior: "instant", block: "start" });
      });
    });
  }, []);

  const handleShowLessWithScroll = useCallback(() => {
    handleShowLess();
    scrollToDefaultView();
  }, [handleShowLess, scrollToDefaultView]);

  const primaryTabs = [
    ...(canShowStocksTab
      ? [{ label: uiConfig.tabs.stocks, value: "stocks" as const }]
      : []),
    ...(canShowFoTab ? [{ label: uiConfig.tabs.fo, value: "fo" as const }] : []),
  ];

  const getDurationFilterLabelForActiveTab = useCallback(
    (filter: TrendingStocksDurationFilter) => {
      if (activeTab === "fo" && filter === "long_term") {
        return "Intraday";
      }
      return uiConfig.durationFilters[filter];
    },
    [activeTab, uiConfig.durationFilters],
  );

  const sectionTitle =
    uiConfig.watchlist?.section_title ?? "Trending stocks";

  const trackSkuDetailsClick = useCallback(
    async (
      clickType: string,
      ctaText: string,
      tabPosition: string,
      subSection?: string,
    ) => {
      const customerId = await getCustomerIdForAnalytics();
      await trackComponent({
        componentName: trendingStocksTrackAnalytics.componentNameCarousel,
        componentId: trendingStocksTrackAnalytics.referenceIdLinkClick,
        sectionTitle,
        eventType: dlEventConstants.CLICK,
        clickType,
        ctaText,
        tabPosition,
        productCategory: trendingStocksTrackAnalytics.productCategory,
        subSection,
        cleverTapOverrides: {
          EP_PROPERTY_TYPE: trendingStocksTrackAnalytics.propertyType,
          EP_PRODUCT_NAME: trendingStocksTrackAnalytics.productName,
          EP_CAMPAIGN: trendingStocksTrackAnalytics.campaign,
          EP_SPRINT: trendingStocksTrackAnalytics.sprint,
          EP_REFERENCE_ID: trendingStocksTrackAnalytics.referenceIdLinkClick,
          ...(customerId && { EP_CUSTOMER_ID: customerId }),
          EP_SR_NUMBER: tabPosition,
          EP_ENTRYPOINT: trendingStocksTrackAnalytics.entryPoints.plp,
          ...(subSection && { EP_SUB_SECTION: subSection }),
        },
      });
    },
    [sectionTitle],
  );

  const handlePrimaryTabChange = useCallback(
    (value: TrendingStocksPrimaryTab) => {
      if (value === activeTab) return;

      const tabIndex = primaryTabs.findIndex((tab) => tab.value === value);
      const ctaText = primaryTabs[tabIndex]?.label ?? value;
      void (async () => {
        try {
          await trackSkuDetailsClick(
            trendingStocksTrackAnalytics.clickTypes.tabClick,
            ctaText,
            String(tabIndex + 1),
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to track trending stocks primary tab click", error);
        }
      })();
      setActiveTab(value);
    },
    [activeTab, primaryTabs, setActiveTab, trackSkuDetailsClick],
  );

  const handleDurationChange = useCallback(
    (filter: TrendingStocksDurationFilter) => {
      if (filter === activeDuration) return;

      const filterIndex = TRENDING_STOCKS_DURATION_FILTERS.indexOf(filter);
      const ctaText = getDurationFilterLabelForActiveTab(filter);
      const subSection =
        activeTab === "fo" ? uiConfig.tabs.fo : uiConfig.tabs.stocks;

      void (async () => {
        try {
          await trackSkuDetailsClick(
            trendingStocksTrackAnalytics.clickTypes.tabClick,
            ctaText,
            String(filterIndex + 1),
            subSection,
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to track trending stocks duration filter click", error);
        }
      })();
      setActiveDuration(filter);
    },
    [
      activeDuration,
      activeTab,
      getDurationFilterLabelForActiveTab,
      setActiveDuration,
      trackSkuDetailsClick,
      uiConfig.tabs.fo,
      uiConfig.tabs.stocks,
    ],
  );

  const shouldShowError = (hasError || initialHasError) && items.length === 0;
  const shouldShowEmpty = !isLoading && !shouldShowError && items.length === 0;
  const activeSelectionKey = getSelectionKey(activeTab, activeDuration);

  const stockPanels = TRENDING_STOCKS_PRIMARY_TABS.flatMap((tab) =>
    TRENDING_STOCKS_DURATION_FILTERS.map((duration) => {
      const key = getSelectionKey(tab, duration);
      return {
        key,
        tab,
        duration,
        items: initialSelections?.[key]?.items ?? [],
      };
    }),
  ).filter(
    ({ key, items: selectionItems }) =>
      key === activeSelectionKey || selectionItems.length > 0,
  );

  const hasStockPanels = stockPanels.some(
    ({ key, items: selectionItems }) =>
      (key === activeSelectionKey ? items : selectionItems).length > 0,
  );

  return (
    <section
      ref={stocksSectionRef}
      className="scroll-mt-[var(--bfl-header-height-mobile)] md:scroll-mt-[var(--bfl-header-height-desktop)]"
    >
      {primaryTabs.length > 0 && (
        <div className="mt-4 pl-[16px]">
          <BFLTabs
            tabs={primaryTabs}
            selectedTab={activeTab}
            onTabChange={handlePrimaryTabChange}
            className="border-b border-[#F2F2F2]"
            tabClassName="max-md:pb-0 pb-2 [&>p]:font-normal [&:has(span)>p]:font-medium"
            indicatorClassName="bg-[#FF6700] h-[2px] md:h-[2px] md:bottom-0 max-md:bottom-[-1px] max-md:z-[1] max-md:rounded-none"
            paragraphClassName="max-md:pb-2 md:pb-0 text-[12px] md:text-[16px]"
            gapClassName="gap-6 max-md:items-end"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 py-[16px] pl-[16px] shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
        {TRENDING_STOCKS_DURATION_FILTERS.map((filter) => {
          const isActive = activeDuration === filter;
          return (
            <BFLButton
              key={filter}
              onClick={() => handleDurationChange(filter)}
              className={`flex items-center justify-center rounded-[8px] border px-2 py-[5px] md:p-2 text-[14px] font-normal leading-[18px] ${
                isActive
                  ? "border-[#002953] bg-[#002953] text-white"
                  : "border-[#8C9CAD] bg-white text-[#002953]"
              }`}
            >
              {getDurationFilterLabelForActiveTab(filter)}
            </BFLButton>
          );
        })}
      </div>

      {isLoading && items.length === 0 && (
        <div className="mt-3 md:mt-4 pl-4 md:pr-0 flex flex-col gap-3 mr-4 mb-4">
          {[1, 2, 3].map((row) => (
            <BFLSkeleton
              key={`trending-card-skeleton-${row}`}
              height={130}
              classes="rounded-[6px]"
            />
          ))}
        </div>
      )}

      {shouldShowError && (
        <BFLApiFailure
          error={uiConfig.apiFailingState?.title ?? "Unable to fetch news"}
          errorDescription={
            uiConfig.apiFailingState?.description ??
            "There was an issue fetching trending stocks for your portfolio. Please try again later."
          }
          retryButtonText={uiConfig.apiFailingState?.retryButtonText ?? "Retry"}
          imageSrc={uiConfig.apiFailingState?.errorImage}
          imageAlt={uiConfig.apiFailingState?.apiFailureImageAlt ?? "Error"}
          onRetryClick={retryCurrentSelection}
        />
      )}

      {shouldShowEmpty && (
        <div className="px-4 py-8">
          <NoResultsFound
            title={uiConfig.emptyState?.title ?? uiConfig.fields?.noResultsTitle}
            description={
              uiConfig.emptyState?.description ?? uiConfig.fields?.noResultsDescription
            }
            aemImages={
              uiConfig.emptyState?.imageSrc
                ? { noResultsImage: uiConfig.emptyState.imageSrc }
                : undefined
            }
          />
        </div>
      )}

      {!shouldShowError && hasStockPanels && (
        <ListWithShowMore
          showMore={items.length > 0 && showMore}
          showLess={items.length > 0 && showLess}
          onShowMore={() => {
            if (!isLoadingMore) {
              trackShowMoreClick();
              handleShowMore();
            }
          }}
          onShowLess={handleShowLessWithScroll}
          containerClassName="mb-8"
          buttonContainerClassName="px-4 pt-3 max-w-[716px] md:px-0"
          showMoreText={isLoadingMore ? "Loading..." : uiConfig.showMoreText}
          showLessText={uiConfig.showLessText}
        >
          {stockPanels.map(({ key, tab, duration, items: selectionItems }) => {
            const isActiveSelection = key === activeSelectionKey;
            const panelItems = isActiveSelection ? items : selectionItems;
            if (panelItems.length === 0) return null;

            return (
              <div
                key={key}
                className={`mt-3 md:mt-4 pl-4 pr-4 md:pr-0 flex flex-col gap-3 ${
                  isActiveSelection ? "" : "hidden"
                }`}
                data-duration={duration}
                data-tab={tab}
              >
                {panelItems.map((stock, index) => (
                  <TrendingStockCard
                    key={`${key}-${stock.id}-${stock.symbol}-${stock.exchId}-${stock.token}-${index}`}
                    stock={stock}
                    fieldLabels={uiConfig.fields}
                    watchlist={uiConfig.watchlist}
                    buyText={uiConfig.trade.buyText}
                    sellText={uiConfig.trade.sellText}
                    analyticsContext={{
                      sectionTitle:
                        uiConfig.watchlist?.section_title ?? "Trending stocks",
                      entryPoint: trendingStocksTrackAnalytics.entryPoints.plp,
                      componentPosition: (index + 1).toString(),
                      subSection:
                        tab === "fo" ? uiConfig.tabs.fo : uiConfig.tabs.stocks,
                    }}
                    onTradeClick={handleTradeClick}
                  />
                ))}
              </div>
            );
          })}
        </ListWithShowMore>
      )}
    </section>
  );
};

export default TrendingStocksPlpInteractiveClient;
